import { useState, useMemo } from "react";
import { formatDistanceToNowStrict, formatISO, secondsToMilliseconds } from "date-fns";
import { Link, useParams } from "react-router-dom";
import {
  ArrowDownTrayIcon, ChevronDownIcon,
  ChevronUpIcon
} from "@heroicons/react/20/solid";
import { DocumentPlusIcon, QuestionMarkCircleIcon } from "@heroicons/react/24/outline";
import { BotIcon, CheckSquareIcon, EyeIcon, MessageCircleCheckIcon } from "lucide-react";
import {
  DealActivityFragmentFragment,
  DealActivityType,
} from "../../graphql/generated";
import { classNames } from "../../utils/cn";
import { ActivityItem, ActivityItemMode } from "./ActivityItem";
import { FileLink } from "./FileLink";
import { FilePill } from "../FilePill";
import {
  ActivityGroup,
  DdqlTreeNode,
  buildDdqlTree,
  countTreeActivities,
  countTreeFiles,
  flattenTreeActivities,
  isFileCreatedGroup,
  isFileViewOrDownloadGroup,
  isQuestionAnsweredGroup,
  isQuestionAskedGroup,
  isQuestionDocumentsFoundGroup,
  isTaskCreatedGroup,
} from "../../utils/activityGrouping";

interface GroupedActivityItemProps {
  group: ActivityGroup;
  isFirst: boolean;
  isLast: boolean;
  mode?: ActivityItemMode;
}

function FileViewDownloadSummary({ group }: { group: ActivityGroup }) {
  const { activities } = group;
  const accountName = activities[0]?.account?.name ?? "Someone";

  const viewCount = activities.filter(
    (a) => a.type === DealActivityType.FileViewed,
  ).length;
  const downloadCount = activities.filter(
    (a) => a.type === DealActivityType.FileDownloaded,
  ).length;

  const totalCount = activities.length;
  const documentWord = totalCount === 1 ? "document" : "documents";

  let action = "viewed";
  if (viewCount > 0 && downloadCount > 0) action = "viewed + downloaded";
  else if (downloadCount > 0) action = "downloaded";

  return (
    <p className="text-sm leading-5 text-persian-990 truncate">
      <span className="font-semibold text-persian-990">{accountName}</span>
      {` ${action} `}
      <span className="font-semibold text-persian-990">
        {totalCount} {documentWord}
      </span>
    </p>
  );
}

function FileCreatedSummary({ group }: { group: ActivityGroup }) {
  const { dealId } = useParams<{ dealId: string }>();
  const { activities } = group;
  const accountName = activities[0]?.account?.name ?? "Someone";
  const count = activities.length;
  const documentWord = count === 1 ? "document" : "documents";
  const folder = activities[0]?.folder;
  const folderName = folder?.name || "Home";

  return (
    <p className="text-sm leading-5 text-persian-990 truncate">
      <span className="font-semibold text-persian-990">{accountName}</span>
      {" uploaded "}
      <span className="font-semibold text-persian-990">
        {count} {documentWord}
      </span>
      {folder && (
        <>
          {" to "}
          <Link
            to={`/deal/${dealId}/documents/folder/${folder.id}`}
            className="font-semibold text-persian-800 hover:text-persian-700"
            onClick={(e) => e.stopPropagation()}
          >
            {folderName}
          </Link>
        </>
      )}
    </p>
  );
}

function DefaultGroupSummary({ group }: { group: ActivityGroup }) {
  const { activities } = group;
  const accountName = activities[0]?.account?.name ?? "Someone";
  const count = activities.length;
  const type = activities[0]?.type;
  const documentWord = count === 1 ? "document" : "documents";

  let action: string;
  switch (type) {
    case DealActivityType.FileDeleted:
      action = "deleted";
      break;
    case DealActivityType.FileViewed:
      action = "viewed";
      break;
    case DealActivityType.FileDownloaded:
      action = "downloaded";
      break;
    default:
      return (
        <p className="text-sm leading-5 text-persian-990 truncate">
          <span className="font-semibold text-persian-990">{accountName}</span>
          {` (${count} activities)`}
        </p>
      );
  }

  return (
    <p className="text-sm leading-5 text-persian-990 truncate">
      <span className="font-semibold text-persian-990">{accountName}</span>
      {` ${action} `}
      <span className="font-semibold text-persian-990">
        {count} {documentWord}
      </span>
    </p>
  );
}

function TaskCreatedSummary({ group }: { group: ActivityGroup }) {
  const { activities } = group;
  const accountName = activities[0]?.account?.name ?? "Someone";
  const count = activities.length;
  const taskWord = count === 1 ? "task" : "tasks";

  return (
    <p className="text-sm leading-5 text-persian-990 truncate">
      <span className="font-semibold text-persian-990">{accountName}</span>
      {" created "}
      <span className="font-semibold text-persian-990">
        {count} {taskWord}
      </span>
    </p>
  );
}

function SubActivityDotItem({
  activity,
  isLast = false,
}: {
  activity: DealActivityFragmentFragment;
  isLast?: boolean;
}) {
  const { dealId } = useParams<{ dealId: string }>();
  const name = activity.account?.name ?? "Someone";

  const content = (
    <p className="text-sm leading-5 text-persian-990">
      <span className="font-semibold text-persian-990">{name}</span>{" "}
      {activity.type === DealActivityType.FileViewed && activity.file && (
        <>
          viewed{" "}
          <FileLink id={activity.file.id} name={activity.file.name} folderId={activity.file.folderId} />
        </>
      )}
      {activity.type === DealActivityType.FileDownloaded && activity.file && (
        <>
          downloaded{" "}
          <FileLink id={activity.file.id} name={activity.file.name} folderId={activity.file.folderId} />
        </>
      )}
      {activity.type === DealActivityType.FileCreated && activity.file && (
        <>
          uploaded{" "}
          <FileLink id={activity.file.id} name={activity.file.name} folderId={activity.file.folderId} />
        </>
      )}
      {activity.type === DealActivityType.FileDeleted && (
        <>
          deleted{" "}
          {activity.file ? (
            <FileLink id={activity.file.id} name={activity.file.name} folderId={activity.file.folderId} />
          ) : (
            "a file"
          )}
        </>
      )}
      {activity.type === DealActivityType.FolderCreated && activity.folder && (
        <>
          added{" "}
          <Link
            to={`/deal/${dealId}/documents/folder/${activity.folder.id}`}
            className="font-semibold text-persian-800 hover:text-persian-700"
          >
            {activity.folder.name}
          </Link>
        </>
      )}
      {activity.type === DealActivityType.FolderDeleted && activity.folder && (
        <>
          deleted folder{" "}
          <span className="font-semibold text-persian-800">
            {activity.folder.name}
          </span>
        </>
      )}
      {activity.type === DealActivityType.QuestionAsked && activity.question && (
        <>
          asked{" "}
          <Link
            to={`/deal/${dealId}/questions/${activity.question.id}`}
            className="font-semibold text-persian-800 hover:text-persian-700"
          >
            {activity.question.title}
          </Link>
        </>
      )}
      {activity.type === DealActivityType.QuestionAnswered && activity.question && (
        <>
          answered{" "}
          <Link
            to={`/deal/${dealId}/questions/${activity.question.id}`}
            className="font-semibold text-persian-800 hover:text-persian-700"
          >
            {activity.question.title}
          </Link>
        </>
      )}
      {activity.type === DealActivityType.TaskCreated && activity.task && (
        <>
          created{" "}
          <Link
            to={`/deal/${dealId}/tasks/${activity.task.id}`}
            className="font-semibold text-persian-800 hover:text-persian-700"
          >
            {activity.task.title}
          </Link>
        </>
      )}
    </p>
  );

  return (
    <div className="flex gap-x-2 items-start">
      <div className="flex flex-col items-center self-stretch shrink-0 w-2 pt-1.5">
        <div className="h-2 w-2 shrink-0 rounded-full bg-persian-200" />
        {!isLast && <div className="flex-1 w-px bg-persian-100 mt-1.5" />}
      </div>
      <div className="flex-1 min-w-0 pb-2">
        {content}
        <time
          title={formatISO(secondsToMilliseconds(activity.createdAt))}
          dateTime={formatISO(secondsToMilliseconds(activity.createdAt))}
          className="block text-xs leading-5 text-concrete-600 mt-0.5"
        >
          {formatDistanceToNowStrict(secondsToMilliseconds(activity.createdAt), {
            addSuffix: true,
          })}
        </time>
      </div>
    </div>
  );
}

function QuestionDocumentsFoundSummary({
  group,
}: {
  group: ActivityGroup;
}) {
  const { dealId } = useParams<{ dealId: string }>();
  const { activities } = group;
  const tree = buildDdqlTree(activities);
  const ddqlId = tree?.ddqlId;
  const ddqlName = tree?.ddqlName;
  const totalFiles = activities.reduce(
    (sum, a) => sum + (a.questionDocumentsFoundData?.files.length ?? 0),
    0,
  );
  const questionCount = activities.length;
  const fileWord = totalFiles === 1 ? "document" : "documents";
  const questionWord = questionCount === 1 ? "question" : "questions";

  return (
    <p className="text-sm leading-5 text-persian-990">
      <span className="font-semibold text-persian-990">Liquid AI</span>
      {" found "}
      {fileWord} for {questionCount} {questionCount > 1 ? " of the " : ""} {questionWord}
      {ddqlName && ddqlId && (
        <>
          {" in "}
          <Link
            to={`/deal/${dealId}/questions/lists/${ddqlId}`}
            className="font-semibold text-persian-800 hover:text-persian-700"
            onClick={(e) => e.stopPropagation()}
          >
            {ddqlName}
          </Link>
        </>
      )}
    </p>
  );
}

function QuestionDocumentsFoundLeaf({
  activity,
  isLast = false,
}: {
  activity: DealActivityFragmentFragment;
  isLast?: boolean;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { dealId } = useParams<{ dealId: string }>();

  if (!activity.question || !activity.questionDocumentsFoundData) {
    return null;
  }

  const fileCount = activity.questionDocumentsFoundData.files.length;

  return (
    <div className="flex gap-x-2 items-start">
      <div className="flex flex-col items-center self-stretch shrink-0 w-2 pt-1.5">
        <div className="h-2 w-2 shrink-0 rounded-full bg-persian-200" />
        {!isLast && <div className="flex-1 w-px bg-persian-100 mt-1.5" />}
      </div>
      <div className="flex-1 min-w-0 pb-2">
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex w-full items-center justify-between gap-x-2 text-left"
        >
          <p className="text-sm leading-5 text-persian-990">
            Found{" "}
            <span className="font-semibold text-persian-990">
              {fileCount} {fileCount === 1 ? "file" : "files"}
            </span>
            {" for "}
            <Link
              to={`/deal/${dealId}/questions/${activity.question.id}`}
              className="font-semibold text-persian-800 hover:text-persian-700"
              onClick={(e) => e.stopPropagation()}
            >
              {activity.question.title}
            </Link>
          </p>
          {isExpanded ? (
            <ChevronUpIcon className="h-3.5 w-3.5 flex-shrink-0 text-concrete-400" />
          ) : (
            <ChevronDownIcon className="h-3.5 w-3.5 flex-shrink-0 text-concrete-400" />
          )}
        </button>
        {isExpanded && (
          <div className="flex flex-wrap gap-1 mt-1">
            {activity.questionDocumentsFoundData.files.map((file) => (
              <div
                key={file.id}
                className="inline-flex h-7 items-center gap-2 px-0.5 py-1"
              >
                <FilePill
                  id={file.id}
                  name={file.name}
                  type={file.fileType}
                />
              </div>
            ))}
          </div>
        )}
        <time
          title={formatISO(secondsToMilliseconds(activity.createdAt))}
          dateTime={formatISO(secondsToMilliseconds(activity.createdAt))}
          className="block text-xs leading-5 text-concrete-600 mt-0.5"
        >
          {formatDistanceToNowStrict(
            secondsToMilliseconds(activity.createdAt),
            { addSuffix: true },
          )}
        </time>
      </div>
    </div>
  );
}

const MAX_DDQL_DEPTH = 3;

/**
 * Renders a DDQL tree node. At each level:
 * - Sub-DDQLs render as collapsible groups (dot + name + question count + chevron)
 * - Direct activities render as QuestionDocumentsFoundLeaf items
 * After MAX_DDQL_DEPTH, all remaining activities are flattened as leaves.
 */
function DdqlTreeNodeItem({
  node,
  depth,
  isLast = false,
}: {
  node: DdqlTreeNode;
  depth: number;
  isLast?: boolean;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const questionCount = countTreeActivities(node);
  const fileCount = countTreeFiles(node);
  const questionWord = questionCount === 1 ? "question" : "questions";

  // At or beyond max depth, flatten everything as leaf items
  if (depth >= MAX_DDQL_DEPTH) {
    const allActivities = flattenTreeActivities(node);
    return (
      <>
        {allActivities.map((activity, i) => (
          <QuestionDocumentsFoundLeaf
            key={activity.id}
            activity={activity}
            isLast={isLast && i === allActivities.length - 1}
          />
        ))}
      </>
    );
  }

  // Collapsible sub-DDQL group
  return (
    <div className="flex gap-x-2 items-start">
      <div className="flex flex-col items-center self-stretch shrink-0 w-2 pt-1.5">
        <div className="h-2 w-2 shrink-0 rounded-full bg-persian-200" />
        {!isLast && <div className="flex-1 w-px bg-persian-100 mt-1.5" />}
      </div>
      <div className="flex-1 min-w-0 pb-1">
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex w-full items-center justify-between gap-x-2 rounded-md pr-1 text-left transition-colors hover:bg-concrete-50"
        >
          <p className="text-sm leading-5 text-persian-990">
            <span className="font-semibold text-persian-990">
              {node.ddqlName}
            </span>
            {" "}
            ({fileCount} {fileCount === 1 ? "file" : "files"} for {questionCount} {questionWord})
          </p>
          {isExpanded ? (
            <ChevronUpIcon className="h-3.5 w-3.5 flex-shrink-0 text-concrete-400" />
          ) : (
            <ChevronDownIcon className="h-3.5 w-3.5 flex-shrink-0 text-concrete-400" />
          )}
        </button>
        {isExpanded && (
          <div className="mt-1">
            <DdqlTreeChildren node={node} depth={depth} isLastParent={isLast} />
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Renders the children of a DdqlTreeNode:
 * sub-DDQL groups first, then direct activity leaves.
 */
function DdqlTreeChildren({
  node,
  depth,
  isLastParent = false,
}: {
  node: DdqlTreeNode;
  depth: number;
  isLastParent?: boolean;
}) {
  const items: Array<
    | { kind: "ddql"; node: DdqlTreeNode }
    | { kind: "activity"; activity: DealActivityFragmentFragment }
  > = [];

  for (const child of node.children) {
    items.push({ kind: "ddql", node: child });
  }
  for (const activity of node.activities) {
    items.push({ kind: "activity", activity });
  }

  return (
    <>
      {items.map((item, i) => {
        const isLastItem = i === items.length - 1;
        if (item.kind === "ddql") {
          return (
            <DdqlTreeNodeItem
              key={item.node.ddqlId}
              node={item.node}
              depth={depth + 1}
              isLast={isLastParent && isLastItem}
            />
          );
        }
        return (
          <QuestionDocumentsFoundLeaf
            key={item.activity.id}
            activity={item.activity}
            isLast={isLastParent && isLastItem}
          />
        );
      })}
    </>
  );
}

function QuestionDocumentsFoundExpandedContent({
  group,
}: {
  group: ActivityGroup;
}) {
  const tree = useMemo(
    () => buildDdqlTree(group.activities),
    [group.activities],
  );

  if (!tree) return null;

  return <DdqlTreeChildren node={tree} depth={1} isLastParent />;
}

function QuestionActivitySummary({
  group,
  action,
}: {
  group: ActivityGroup;
  action: string;
}) {
  const { dealId } = useParams<{ dealId: string }>();
  const { activities } = group;
  const accountName = activities[0]?.account?.name ?? "Someone";
  const tree = buildDdqlTree(activities);
  const ddqlId = tree?.ddqlId;
  const ddqlName = tree?.ddqlName;
  const count = activities.length;
  const questionWord = count === 1 ? "question" : "questions";

  return (
    <p className="text-sm leading-5 text-persian-990 truncate">
      <span className="font-semibold text-persian-990">{accountName}</span>
      {` ${action} `}
      <span className="font-semibold text-persian-990">
        {count} {questionWord}
      </span>
      {ddqlName && ddqlId && (
        <>
          {" in "}
          <Link
            to={`/deal/${dealId}/questions/lists/${ddqlId}`}
            className="font-semibold text-persian-800 hover:text-persian-700"
            onClick={(e) => e.stopPropagation()}
          >
            {ddqlName}
          </Link>
        </>
      )}
    </p>
  );
}

function QuestionActivityLeaf({
  activity,
  action,
  isLast = false,
}: {
  activity: DealActivityFragmentFragment;
  action: string;
  isLast?: boolean;
}) {
  const { dealId } = useParams<{ dealId: string }>();

  if (!activity.question) return null;

  const name = activity.account?.name ?? "Someone";

  return (
    <div className="flex gap-x-2 items-start">
      <div className="flex flex-col items-center self-stretch shrink-0 w-2 pt-1.5">
        <div className="h-2 w-2 shrink-0 rounded-full bg-persian-200" />
        {!isLast && <div className="flex-1 w-px bg-persian-100 mt-1.5" />}
      </div>
      <div className="flex-1 min-w-0 pb-2">
        <p className="text-sm leading-5 text-persian-990">
          <span className="font-semibold text-persian-990">{name}</span>
          {` ${action} `}
          <Link
            to={`/deal/${dealId}/questions/${activity.question.id}`}
            className="font-semibold text-persian-800 hover:text-persian-700"
          >
            {activity.question.title}
          </Link>
        </p>
        <time
          title={formatISO(secondsToMilliseconds(activity.createdAt))}
          dateTime={formatISO(secondsToMilliseconds(activity.createdAt))}
          className="block text-xs leading-5 text-concrete-600 mt-0.5"
        >
          {formatDistanceToNowStrict(secondsToMilliseconds(activity.createdAt), {
            addSuffix: true,
          })}
        </time>
      </div>
    </div>
  );
}

function QuestionActivityDdqlNodeItem({
  node,
  action,
  depth,
  isLast = false,
}: {
  node: DdqlTreeNode;
  action: string;
  depth: number;
  isLast?: boolean;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const questionCount = countTreeActivities(node);
  const questionWord = questionCount === 1 ? "question" : "questions";

  if (depth >= MAX_DDQL_DEPTH) {
    const allActivities = flattenTreeActivities(node);
    return (
      <>
        {allActivities.map((activity, i) => (
          <QuestionActivityLeaf
            key={activity.id}
            activity={activity}
            action={action}
            isLast={isLast && i === allActivities.length - 1}
          />
        ))}
      </>
    );
  }

  return (
    <div className="flex gap-x-2 items-start">
      <div className="flex flex-col items-center self-stretch shrink-0 w-2 pt-1.5">
        <div className="h-2 w-2 shrink-0 rounded-full bg-persian-200" />
        {!isLast && <div className="flex-1 w-px bg-persian-100 mt-1.5" />}
      </div>
      <div className="flex-1 min-w-0 pb-1">
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex w-full items-center justify-between gap-x-2 rounded-md pr-1 text-left transition-colors hover:bg-concrete-50"
        >
          <p className="text-sm leading-5 text-persian-990">
            <span className="font-semibold text-persian-990">
              {node.ddqlName}
            </span>
            {" "}
            ({questionCount} {questionWord})
          </p>
          {isExpanded ? (
            <ChevronUpIcon className="h-3.5 w-3.5 flex-shrink-0 text-concrete-400" />
          ) : (
            <ChevronDownIcon className="h-3.5 w-3.5 flex-shrink-0 text-concrete-400" />
          )}
        </button>
        {isExpanded && (
          <div className="mt-1">
            <QuestionActivityDdqlChildren node={node} action={action} depth={depth} isLastParent={isLast} />
          </div>
        )}
      </div>
    </div>
  );
}

function QuestionActivityDdqlChildren({
  node,
  action,
  depth,
  isLastParent = false,
}: {
  node: DdqlTreeNode;
  action: string;
  depth: number;
  isLastParent?: boolean;
}) {
  const items: Array<
    | { kind: "ddql"; node: DdqlTreeNode }
    | { kind: "activity"; activity: DealActivityFragmentFragment }
  > = [];

  for (const child of node.children) {
    items.push({ kind: "ddql", node: child });
  }
  for (const activity of node.activities) {
    items.push({ kind: "activity", activity });
  }

  return (
    <>
      {items.map((item, i) => {
        const isLastItem = i === items.length - 1;
        if (item.kind === "ddql") {
          return (
            <QuestionActivityDdqlNodeItem
              key={item.node.ddqlId}
              node={item.node}
              action={action}
              depth={depth + 1}
              isLast={isLastParent && isLastItem}
            />
          );
        }
        return (
          <QuestionActivityLeaf
            key={item.activity.id}
            activity={item.activity}
            action={action}
            isLast={isLastParent && isLastItem}
          />
        );
      })}
    </>
  );
}

function QuestionActivityExpandedContent({
  group,
  action,
}: {
  group: ActivityGroup;
  action: string;
}) {
  const tree = useMemo(
    () => buildDdqlTree(group.activities),
    [group.activities],
  );

  if (!tree) return null;

  return <QuestionActivityDdqlChildren node={tree} action={action} depth={1} isLastParent />;
}

export function GroupedActivityItem({
  group,
  isFirst,
  isLast,
  mode = "verbose",
}: GroupedActivityItemProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { activities } = group;

  // Single item - render ActivityItem directly
  if (activities.length === 1) {
    return (
      <ActivityItem
        activity={activities[0]}
        isFirst={isFirst}
        isLast={isLast}
        mode={mode}
      />
    );
  }

  const mostRecentActivity = activities[activities.length - 1];
  const timestamp = mostRecentActivity.createdAt;

  return (
    <li className="relative flex gap-x-1 items-start">
      <div
        className={classNames(
          isLast ? "h-6" : "-bottom-6",
          "absolute left-0 top-0 flex w-6 justify-center",
        )}
      >
        <div className="w-px bg-persian-100" />
      </div>
      <div className="relative bg-white flex h-6 w-6 flex-none items-center justify-center rounded-full ">
        {isQuestionDocumentsFoundGroup(group) ? (
          <BotIcon className="w-4 h-4 text-persian-600" />
        ) : isQuestionAskedGroup(group) ? (
          <QuestionMarkCircleIcon className="w-4 h-4 text-persian-600" />
        ) : isQuestionAnsweredGroup(group) ? (
          <MessageCircleCheckIcon className="w-4 h-4 text-persian-600" />
        ) : isFileViewOrDownloadGroup(group) ? (
          group.activities.some((a) => a.type === DealActivityType.FileDownloaded) &&
            !group.activities.some((a) => a.type === DealActivityType.FileViewed) ? (
            <ArrowDownTrayIcon className="w-4 h-4 text-persian-600" />
          ) : (
            <EyeIcon className="w-4 h-4 text-persian-600" />
          )
        ) : isFileCreatedGroup(group) ? (
          <DocumentPlusIcon className="w-4 h-4 text-persian-600" />
        ) : isTaskCreatedGroup(group) ? (
          <CheckSquareIcon className="w-4 h-4 text-persian-600" />
        ) : (
          <DocumentPlusIcon className="w-4 h-4 text-persian-600" />
        )}
      </div>
      <div className="flex-1  min-w-0 pt-0.5 ">
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="relative flex w-full items-center justify-between gap-x-2 text-left transition-colors"
        >
          {isQuestionDocumentsFoundGroup(group) ? (
            <QuestionDocumentsFoundSummary group={group} />
          ) : isQuestionAskedGroup(group) ? (
            <QuestionActivitySummary group={group} action="asked" />
          ) : isQuestionAnsweredGroup(group) ? (
            <QuestionActivitySummary group={group} action="answered" />
          ) : isFileViewOrDownloadGroup(group) ? (
            <FileViewDownloadSummary group={group} />
          ) : isFileCreatedGroup(group) ? (
            <FileCreatedSummary group={group} />
          ) : isTaskCreatedGroup(group) ? (
            <TaskCreatedSummary group={group} />
          ) : (
            <DefaultGroupSummary group={group} />
          )}
          <div className="flex items-center gap-x-1 -mr-1">
            <time
              title={formatISO(secondsToMilliseconds(timestamp))}
              dateTime={formatISO(secondsToMilliseconds(timestamp))}
              className="flex-none text-xs leading-5 text-concrete-600"
            >
              {formatDistanceToNowStrict(secondsToMilliseconds(timestamp), {
                addSuffix: true,
              })}
            </time>
            <span className="">
              {isExpanded ? (
                <ChevronUpIcon className="h-4 w-4 text-concrete-400" />
              ) : (
                <ChevronDownIcon className="h-4 w-4 text-concrete-400" />
              )}
            </span>
          </div>
        </button>
        {isExpanded && (
          <div className="mt-1">
            {isQuestionDocumentsFoundGroup(group) ? (
              <QuestionDocumentsFoundExpandedContent group={group} />
            ) : isQuestionAskedGroup(group) ? (
              <QuestionActivityExpandedContent group={group} action="asked" />
            ) : isQuestionAnsweredGroup(group) ? (
              <QuestionActivityExpandedContent group={group} action="answered" />
            ) : (
              activities.map((activity, i) => (
                <SubActivityDotItem
                  key={activity.id}
                  activity={activity}
                  isLast={i === activities.length - 1}
                />
              ))
            )}
          </div>
        )}
      </div>
    </li>
  );
}
